import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, Info } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
  companyPerformanceSettingsInputSchema,
  createCalculationPolicyVersionInputSchema,
  type CompanyPerformanceSettingsInput,
  type CompanyPerformanceSettingsVersionDto,
  type CreateCalculationPolicyVersionInput,
} from "@normax-performance/shared-types";
import { computeStandardAvailableMinutes, toIsoDate } from "@normax-performance/shared-format";
import { useAuth } from "../../app/providers/AuthProvider";
import { calculationPolicyApi, companyPerformanceSettingsApi } from "../../lib/api-client/endpoints";
import { ApiError } from "../../lib/api-client/client";
import { Badge, Button, DateField, Input, Modal, useToast } from "../../shared-ui/primitives";
import { formatDateSr } from "../../lib/format";
import { useLocale } from "../../i18n/LocaleProvider";
import type { SupportedLocale } from "../../i18n/locale";
import { SUPPORT_EMAIL } from "../../shared-ui/support";
import "./SettingsPage.css";

function ZonePreview({ lower, upper }: { lower: number; upper: number }) {
  const { t: tSettings } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const belowW = 42;
  const aboveW = 28;
  const neutralW = Math.max(0, 100 - belowW - aboveW);
  return (
    <div className="nx-settings__zone-preview">
      <div className="nx-settings__zone-bar">
        <div className="nx-settings__zone-seg nx-settings__zone-seg--below" style={{ flexBasis: `${belowW}%` }}>
          {`< ${lower || 0}% — ${tCommon("performanceZone.below")}`}
        </div>
        <div className="nx-settings__zone-seg nx-settings__zone-seg--neutral" style={{ flexBasis: `${neutralW}%` }}>
          {`${lower || 0}%–${upper || 0}% — ${tCommon("performanceZone.neutral")}`}
        </div>
        <div className="nx-settings__zone-seg nx-settings__zone-seg--above" style={{ flexBasis: `${aboveW}%` }}>
          {`> ${upper || 0}% — ${tCommon("performanceZone.above")}`}
        </div>
      </div>
      <p className="nx-field__hint">
        <Trans t={tSettings} i18nKey="zonePreview.example" components={{ strong: <strong /> }} />
      </p>
    </div>
  );
}

/**
 * Language selector — deliberately OUTSIDE `<fieldset disabled={readOnly}>`:
 * UI language is a per-browser preference (LocaleProvider, i18n architecture
 * plan §7), NOT a company performance-rule setting, so it must work
 * identically for ADMIN and DIRECTOR alike and must never be gated by the
 * `readOnly` role check below.
 */
function LanguageSection() {
  const { t: tSettings } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const { locale, setLocale, languageOptions } = useLocale();
  return (
    <section className="nx-settings__section">
      <h2>{tSettings("language.sectionTitle")}</h2>
      <div className="nx-field">
        <label className="nx-field__label" htmlFor="nx-settings-locale-select">
          {tSettings("language.label")}
        </label>
        <select
          id="nx-settings-locale-select"
          className="nx-input"
          value={locale}
          onChange={(e) => setLocale(e.target.value as SupportedLocale)}
        >
          {languageOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {tCommon(opt.labelKey)}
            </option>
          ))}
        </select>
        <span className="nx-field__hint">{tSettings("language.helperText")}</span>
      </div>
    </section>
  );
}

/**
 * Support contact — deliberately OUTSIDE `<fieldset disabled={readOnly}>`,
 * same reasoning as LanguageSection: this is not a company performance-rule
 * setting, must be visible/identical for ADMIN and DIRECTOR alike
 * (docs/NORMAX_PERFORMANCE_PWA_AND_BRANDING_IMPLEMENTATION_REPORT.md §10).
 */
function SupportSection() {
  const { t: tSettings } = useTranslation("settings");
  return (
    <section className="nx-settings__section">
      <h2>{tSettings("support.sectionTitle")}</h2>
      <p>
        {tSettings("support.label")}: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </p>
    </section>
  );
}

/**
 * Podešavanja → Obračun učinka — self-service ADMIN flow for approving/
 * re-approving the tenant's Calculation Policy Version, closing the P0
 * operability gap identified in
 * NORMAX_PERFORMANCE_DAILY_ENTRY_FINALIZATION_UX_FIX_REPORT.md ("Idi na
 * Podešavanja" previously led nowhere actionable).
 *
 * Deliberately NOT a form over `formulaSpec`/`roundingSpec`/
 * `aggregationMethod` — those are fixed, code-derived constants (Method C
 * is locked, never edited here); the only genuinely admin-decided fields
 * are `effectiveFrom` and an optional `notes`. See
 * calculation-policy.logic.ts (apps/api) for the full reasoning and
 * packages/shared-types/calculation-policy-settings.ts's header.
 *
 * Independent `<form>`/mutation from the Company Performance Settings form
 * above — separate backend entity, separate submit action, separate
 * version history — never merged into one combined form.
 */
function CalculationPolicySection({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  const sectionRef = useRef<HTMLElement>(null);

  // "Idi na Podešavanja" from a blocked Daily Entry finalization
  // (DailyEntryPage.tsx) navigates here with this hash — same pattern as
  // the work-time section's own hash-scroll below.
  useEffect(() => {
    if (location.hash === "#nx-settings-calcpolicy-section") {
      sectionRef.current?.scrollIntoView({ block: "start" });
      sectionRef.current?.focus();
    }
  }, [location.hash]);

  const currentQuery = useQuery({
    queryKey: ["calculation-policy", "current"],
    queryFn: () => calculationPolicyApi.current(),
  });
  const versionsQuery = useQuery({
    queryKey: ["calculation-policy", "versions"],
    queryFn: () => calculationPolicyApi.versions(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateCalculationPolicyVersionInput>({
    resolver: zodResolver(createCalculationPolicyVersionInputSchema),
    defaultValues: { effectiveFrom: toIsoDate(new Date()), notes: "" },
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateCalculationPolicyVersionInput) => calculationPolicyApi.createVersion(input),
    onSuccess: () => {
      showToast({ title: t("calculationPolicy.toast.saveSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["calculation-policy"] });
      reset({ effectiveFrom: toIsoDate(new Date()), notes: "" });
    },
    onError: (error) => {
      showToast({
        title: t("calculationPolicy.toast.saveErrorTitle"),
        description: error instanceof ApiError ? error.message : tCommon("dateField.invalidFormat"),
        tone: "danger",
      });
    },
  });

  const current = currentQuery.data ?? null;

  return (
    <section
      id="nx-settings-calcpolicy-section"
      tabIndex={-1}
      ref={sectionRef}
      className="nx-settings__section"
    >
      <h2>{t("calculationPolicy.sectionTitle")}</h2>

      {currentQuery.isLoading ? (
        <p className="nx-field__hint">{tCommon("loading")}</p>
      ) : current ? (
        <p className="nx-field__hint">
          {t("calculationPolicy.currentLabel")}:{" "}
          {t("calculationPolicy.currentValue", {
            date: formatDateSr(current.approvedAt ?? current.effectiveFrom),
          })}
        </p>
      ) : (
        <div className="nx-settings__notice">
          <Info size={18} aria-hidden="true" />
          <div>
            <p>{t("calculationPolicy.notConfiguredTitle")}</p>
            <p>
              {readOnly
                ? t("calculationPolicy.notConfiguredBodyDirector")
                : t("calculationPolicy.notConfiguredBodyAdmin")}
            </p>
          </div>
        </div>
      )}

      {!readOnly ? (
        <form
          className="nx-settings__field-row"
          onSubmit={handleSubmit((values) => createMutation.mutate(values))}
        >
          <Controller
            name="effectiveFrom"
            control={control}
            render={({ field }) => (
              <DateField
                label={t("calculationPolicy.field.effectiveFrom")}
                error={errors.effectiveFrom?.message}
                value={field.value}
                onChange={field.onChange}
                invalidFormatMessage={tCommon("dateField.invalidFormat")}
                calendarButtonLabel={tCommon("dateField.openCalendar")}
                previousMonthLabel={tCommon("dateField.previousMonth")}
                nextMonthLabel={tCommon("dateField.nextMonth")}
              />
            )}
          />
          <Input
            label={t("calculationPolicy.field.notes")}
            error={errors.notes?.message}
            {...register("notes")}
          />
          <Button type="submit" variant="primary" loading={createMutation.isPending}>
            <Check size={16} />
            {current ? t("calculationPolicy.reapproveAction") : t("calculationPolicy.configureAction")}
          </Button>
        </form>
      ) : null}

      <div className="nx-settings__section">
        <h3>{t("calculationPolicy.history.title")}</h3>
        {versionsQuery.isLoading ? (
          <p className="nx-field__hint">{tCommon("loading")}</p>
        ) : versionsQuery.data && versionsQuery.data.length > 0 ? (
          <table className="nx-settings__history-table">
            <thead>
              <tr>
                <th>{t("calculationPolicy.history.column.effectiveFrom")}</th>
                <th>{t("calculationPolicy.history.column.approvedAt")}</th>
                <th>{t("calculationPolicy.history.column.notes")}</th>
              </tr>
            </thead>
            <tbody>
              {versionsQuery.data.map((v) => (
                <tr key={v.id}>
                  <td>
                    {formatDateSr(v.effectiveFrom)}{" "}
                    {v.isActive ? (
                      <Badge tone="success" icon={<CheckCircle2 size={12} aria-hidden />}>
                        {t("calculationPolicy.history.active")}
                      </Badge>
                    ) : null}
                  </td>
                  <td>{v.approvedAt ? formatDateSr(v.approvedAt) : "—"}</td>
                  <td>{v.notes ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="nx-field__hint">{t("calculationPolicy.history.empty")}</p>
        )}
      </div>
    </section>
  );
}

/**
 * Podešavanja → Pravila učinka i radno vreme → Podešavanje preporuke normi
 * — a compact summary line + modal rather than 5 more inline fields in the
 * already-dense Settings form (user's explicit UX request). Writes through
 * the SAME `createMutation`/`CompanyPerformanceSettingsVersion` entity as
 * the rest of the form — never a second, independent mutation — so its
 * toast/cache-invalidation behavior is identical.
 */
function NormRecommendationSettingsModal({
  current,
  onClose,
  onSubmit,
  isPending,
}: {
  current: CompanyPerformanceSettingsVersionDto;
  onClose: () => void;
  onSubmit: (input: CompanyPerformanceSettingsInput) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const [values, setValues] = useState({
    normRecommendationLowerBound: current.normRecommendationLowerBound,
    normRecommendationUpperBound: current.normRecommendationUpperBound,
    normRecommendationMinLines: current.normRecommendationMinLines,
    normRecommendationMinWorkers: current.normRecommendationMinWorkers,
    effectiveFrom: toIsoDate(new Date()),
    changeReason: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof values, string>>>({});

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = companyPerformanceSettingsInputSchema.safeParse({
      grossShiftMinutes: current.grossShiftMinutes,
      standardBreakMinutes: current.standardBreakMinutes,
      lowerNeutralThreshold: current.lowerNeutralThreshold,
      aboveNormThreshold: current.aboveNormThreshold,
      rankingMinNormedMinutes: current.rankingMinNormedMinutes,
      ...values,
    });
    if (!result.success) {
      // Same field-mapping convention as NormsTab.tsx's NormFormModal — a
      // failed submit must surface WHY, not silently do nothing.
      const fieldErrors: Partial<Record<keyof typeof values, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof typeof values | undefined;
        if (field && field in values && !fieldErrors[field]) fieldErrors[field] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    onSubmit(result.data);
  }

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("normRecommendation.modalTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tCommon("cancel")}
          </Button>
          <Button type="submit" form="nx-norm-recommendation-form" variant="primary" loading={isPending}>
            {t("submit")}
          </Button>
        </>
      }
    >
      <form id="nx-norm-recommendation-form" onSubmit={handleSubmit}>
        <div className="nx-settings__field-row">
          <Input
            label={t("normRecommendation.field.lowerBound")}
            type="number"
            hint={t("unit.percent")}
            error={errors.normRecommendationLowerBound}
            value={values.normRecommendationLowerBound}
            onChange={(e) => setValues((v) => ({ ...v, normRecommendationLowerBound: Number(e.target.value) }))}
          />
          <Input
            label={t("normRecommendation.field.upperBound")}
            type="number"
            hint={t("unit.percent")}
            error={errors.normRecommendationUpperBound}
            value={values.normRecommendationUpperBound}
            onChange={(e) => setValues((v) => ({ ...v, normRecommendationUpperBound: Number(e.target.value) }))}
          />
        </div>
        <div className="nx-settings__field-row">
          <Input
            label={t("normRecommendation.field.minLines")}
            type="number"
            error={errors.normRecommendationMinLines}
            value={values.normRecommendationMinLines}
            onChange={(e) => setValues((v) => ({ ...v, normRecommendationMinLines: Number(e.target.value) }))}
          />
          <Input
            label={t("normRecommendation.field.minWorkers")}
            type="number"
            error={errors.normRecommendationMinWorkers}
            value={values.normRecommendationMinWorkers}
            onChange={(e) => setValues((v) => ({ ...v, normRecommendationMinWorkers: Number(e.target.value) }))}
          />
        </div>
        <div className="nx-settings__field-row">
          <DateField
            label={t("field.effectiveFrom")}
            error={errors.effectiveFrom}
            value={values.effectiveFrom}
            onChange={(iso) => setValues((v) => ({ ...v, effectiveFrom: iso }))}
            invalidFormatMessage={tCommon("dateField.invalidFormat")}
            calendarButtonLabel={tCommon("dateField.openCalendar")}
            previousMonthLabel={tCommon("dateField.previousMonth")}
            nextMonthLabel={tCommon("dateField.nextMonth")}
          />
          <Input
            label={t("field.changeReason")}
            error={errors.changeReason}
            value={values.changeReason}
            onChange={(e) => setValues((v) => ({ ...v, changeReason: e.target.value }))}
          />
        </div>
      </form>
    </Modal>
  );
}

/**
 * Podešavanja → Pravila učinka i radno vreme
 * (COMPANY_PERFORMANCE_AND_WORK_TIME_SETTINGS_CONTRACT.md §G,
 * COMPANY_PERFORMANCE_SETTINGS_IMPLEMENTATION_REPORT.md).
 *
 * Wired to the real `company-performance-settings` API — no local/demo
 * fallback state. ADMIN can create a new version (always additive, never
 * edits history); DIRECTOR sees the same data read-only.
 */
export function SettingsPage() {
  const { t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  // Norm recommendations RBAC change (2026-10-08): DIRECTOR now has full
  // edit parity with ADMIN on Company Performance Settings — but NOT on
  // Calculation Policy, which stays its own, narrower ADMIN-only gate.
  const performanceSettingsReadOnly = user?.role !== "ADMIN" && user?.role !== "DIRECTOR";
  const calculationPolicyReadOnly = user?.role !== "ADMIN";
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  const workTimeSectionRef = useRef<HTMLElement>(null);
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);

  // "Idi na Podešavanja" from a blocked Norm form (NormsTab.tsx) navigates
  // here with this hash so the admin lands directly on the work-time
  // section instead of a bare page top — see NORM_FORM_UX_FIX plan §4.
  useEffect(() => {
    if (location.hash === "#nx-settings-worktime-section") {
      workTimeSectionRef.current?.scrollIntoView({ block: "start" });
      workTimeSectionRef.current?.focus();
    }
  }, [location.hash]);

  const currentQuery = useQuery({
    queryKey: ["company-performance-settings", "current"],
    queryFn: () => companyPerformanceSettingsApi.current(),
  });
  const versionsQuery = useQuery({
    queryKey: ["company-performance-settings", "versions"],
    queryFn: () => companyPerformanceSettingsApi.versions(),
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<CompanyPerformanceSettingsInput>({
    resolver: zodResolver(companyPerformanceSettingsInputSchema),
    defaultValues: {
      grossShiftMinutes: 480,
      standardBreakMinutes: 30,
      lowerNeutralThreshold: 90,
      aboveNormThreshold: 100,
      rankingMinNormedMinutes: 120,
      effectiveFrom: toIsoDate(new Date()),
      changeReason: "",
    },
  });

  // Once the active version loads, prefill the form with it (editing starts
  // from the current rules, not from arbitrary defaults).
  useEffect(() => {
    const cs = currentQuery.data;
    if (!cs) return;
    reset({
      grossShiftMinutes: cs.grossShiftMinutes,
      standardBreakMinutes: cs.standardBreakMinutes,
      lowerNeutralThreshold: cs.lowerNeutralThreshold,
      aboveNormThreshold: cs.aboveNormThreshold,
      rankingMinNormedMinutes: cs.rankingMinNormedMinutes,
      effectiveFrom: toIsoDate(new Date()),
      changeReason: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuery.data?.id]);

  const createMutation = useMutation({
    mutationFn: (input: CompanyPerformanceSettingsInput) => companyPerformanceSettingsApi.createVersion(input),
    onSuccess: () => {
      showToast({ title: t("toast.saveSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: ["company-performance-settings"] });
    },
    onError: (error) => {
      showToast({
        title: t("toast.saveErrorTitle"),
        // error.message is backend-authored text when it's an ApiError — pass
        // through unchanged (out of i18n scope, plan §1.5/§9); only the
        // frontend-owned fallback below is translated.
        description: error instanceof ApiError ? error.message : t("toast.saveErrorNoConnection"),
        tone: "danger",
      });
    },
  });

  const grossShiftMinutes = Number(watch("grossShiftMinutes")) || 0;
  const standardBreakMinutes = Number(watch("standardBreakMinutes")) || 0;
  const lowerNeutralThreshold = Number(watch("lowerNeutralThreshold")) || 0;
  const aboveNormThreshold = Number(watch("aboveNormThreshold")) || 0;
  const computedFund = computeStandardAvailableMinutes({ grossShiftMinutes, standardBreakMinutes });

  const cs = currentQuery.data;

  return (
    <div className="nx-settings">
      <h1 className="nx-settings__title">{t("title")}</h1>
      {cs ? (
        <p className="nx-settings__subtitle">
          <Trans
            t={t}
            i18nKey="activeVersion"
            values={{ version: cs.version, date: formatDateSr(cs.effectiveFrom) }}
            components={{ strong: <strong /> }}
          />
        </p>
      ) : currentQuery.isLoading ? (
        <p className="nx-settings__subtitle">{tCommon("loading")}</p>
      ) : (
        <p className="nx-settings__subtitle">{t("noActiveVersion")}</p>
      )}

      <LanguageSection />
      <SupportSection />

      {performanceSettingsReadOnly ? (
        <div className="nx-settings__notice">
          <Info size={18} aria-hidden="true" />
          <p>
            <Trans t={t} i18nKey="readOnlyNotice" components={{ strong: <strong /> }} />
          </p>
        </div>
      ) : (
        <div className="nx-settings__notice">
          <Info size={18} aria-hidden="true" />
          <p>
            <Trans t={t} i18nKey="writeNotice" components={{ strong: <strong /> }} />
          </p>
        </div>
      )}

      <form
        onSubmit={handleSubmit((values) => {
          createMutation.mutate(values);
        })}
      >
        <fieldset disabled={performanceSettingsReadOnly} className="nx-settings__fieldset">
          <section
            id="nx-settings-worktime-section"
            tabIndex={-1}
            ref={workTimeSectionRef}
            className="nx-settings__section"
          >
            <h2>{t("section.workTime")}</h2>
            <div className="nx-settings__field-row">
              <Input
                label={t("field.grossShiftMinutes")}
                type="number"
                hint={t("unit.minutes")}
                error={errors.grossShiftMinutes?.message}
                {...register("grossShiftMinutes", { valueAsNumber: true })}
              />
              <Input
                label={t("field.standardBreakMinutes")}
                type="number"
                hint={t("unit.minutes")}
                error={errors.standardBreakMinutes?.message}
                {...register("standardBreakMinutes", { valueAsNumber: true })}
              />
              <div className="nx-field">
                <label className="nx-field__label">{t("field.standardAvailableMinutes")}</label>
                <div className="nx-settings__computed">
                  {computedFund} {t("unit.minutes")}
                </div>
                <span className="nx-field__hint">
                  {t("field.standardAvailableMinutesFormula", {
                    gross: grossShiftMinutes,
                    brk: standardBreakMinutes,
                  })}
                </span>
              </div>
            </div>
            <p className="nx-field__hint">
              <Trans t={t} i18nKey="field.standardAvailableMinutesHint" components={{ strong: <strong /> }} />
            </p>
          </section>

          <section className="nx-settings__section">
            <h2>{t("section.performanceZones")}</h2>
            <div className="nx-settings__field-row">
              <Input
                label={t("field.lowerNeutralThreshold")}
                type="number"
                hint={t("unit.percent")}
                error={errors.lowerNeutralThreshold?.message}
                {...register("lowerNeutralThreshold", { valueAsNumber: true })}
              />
              <Input
                label={t("field.aboveNormThreshold")}
                type="number"
                hint={t("unit.percent")}
                error={errors.aboveNormThreshold?.message}
                {...register("aboveNormThreshold", { valueAsNumber: true })}
              />
            </div>
            <ZonePreview lower={lowerNeutralThreshold} upper={aboveNormThreshold} />
            {cs ? (
              <div className="nx-settings__field-row">
                <div className="nx-field">
                  <span className="nx-field__label">{t("normRecommendation.sectionTitle")}</span>
                  <p>
                    {t("normRecommendation.summaryValue", {
                      lower: cs.normRecommendationLowerBound,
                      upper: cs.normRecommendationUpperBound,
                      minLines: cs.normRecommendationMinLines,
                      minWorkers: cs.normRecommendationMinWorkers,
                    })}
                  </p>
                </div>
                {!performanceSettingsReadOnly ? (
                  <Button type="button" variant="primary" size="sm" onClick={() => setShowRecommendationModal(true)}>
                    {t("normRecommendation.editAction")}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="nx-settings__section">
            <h2>{t("section.ranking")}</h2>
            <Input
              label={t("field.rankingMinNormedMinutes")}
              type="number"
              hint={t("field.rankingMinNormedMinutesUnit")}
              error={errors.rankingMinNormedMinutes?.message}
              {...register("rankingMinNormedMinutes", { valueAsNumber: true })}
            />
            <p className="nx-field__hint">
              <Trans t={t} i18nKey="field.rankingHint" components={{ strong: <strong /> }} />
            </p>
          </section>

          <section className="nx-settings__section">
            <h2>{t("section.validity")}</h2>
            <div className="nx-settings__field-row">
              <Controller
                name="effectiveFrom"
                control={control}
                render={({ field }) => (
                  <DateField
                    label={t("field.effectiveFrom")}
                    error={errors.effectiveFrom?.message}
                    value={field.value}
                    onChange={field.onChange}
                    invalidFormatMessage={tCommon("dateField.invalidFormat")}
                    calendarButtonLabel={tCommon("dateField.openCalendar")}
                    previousMonthLabel={tCommon("dateField.previousMonth")}
                    nextMonthLabel={tCommon("dateField.nextMonth")}
                  />
                )}
              />
              <Input
                label={t("field.changeReason")}
                error={errors.changeReason?.message}
                {...register("changeReason")}
              />
            </div>
            <div className="nx-settings__warning">{t("validityWarning")}</div>
          </section>

          {!performanceSettingsReadOnly ? (
            <div className="nx-settings__actions">
              <Button type="submit" variant="primary" loading={createMutation.isPending}>
                <Check size={16} />
                {t("submit")}
              </Button>
            </div>
          ) : null}
        </fieldset>
      </form>

      <div className="nx-settings__section">
        <h2>{t("history.title")}</h2>
        {versionsQuery.isLoading ? (
          <p className="nx-field__hint">{tCommon("loading")}</p>
        ) : versionsQuery.data && versionsQuery.data.length > 0 ? (
          <table className="nx-settings__history-table">
            <thead>
              <tr>
                <th>{t("history.column.version")}</th>
                <th>{t("history.column.workTime")}</th>
                <th>{t("history.column.zones")}</th>
                <th>{t("history.column.ranking")}</th>
                <th>{t("history.column.effectiveFrom")}</th>
                <th>{t("history.column.effectiveTo")}</th>
                <th>{t("history.column.reason")}</th>
              </tr>
            </thead>
            <tbody>
              {versionsQuery.data.map((v) => (
                <tr key={v.id}>
                  <td>
                    {v.version}{" "}
                    {v.isActive ? (
                      <Badge tone="success" icon={<CheckCircle2 size={12} aria-hidden />}>
                        {t("history.active")}
                      </Badge>
                    ) : null}
                  </td>
                  <td>
                    {v.grossShiftMinutes}/{v.standardBreakMinutes}/{v.standardAvailableMinutes}{" "}
                    {t("unit.minutes")}
                  </td>
                  <td>
                    {v.lowerNeutralThreshold}–{v.aboveNormThreshold}
                    {t("unit.percent")}
                  </td>
                  <td>
                    {v.rankingMinNormedMinutes} {t("unit.minutes")}
                  </td>
                  <td>{formatDateSr(v.effectiveFrom)}</td>
                  <td>{v.effectiveTo ? formatDateSr(v.effectiveTo) : "—"}</td>
                  <td>{v.changeReason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="nx-field__hint">{t("history.empty")}</p>
        )}
      </div>

      <CalculationPolicySection readOnly={calculationPolicyReadOnly} />

      {showRecommendationModal && cs ? (
        <NormRecommendationSettingsModal
          current={cs}
          isPending={createMutation.isPending}
          onClose={() => setShowRecommendationModal(false)}
          onSubmit={(input) => {
            createMutation.mutate(input, { onSuccess: () => setShowRecommendationModal(false) });
          }}
        />
      ) : null}
    </div>
  );
}
